import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  InvoicesDataTableDocument,
  InvoicesDataTableQuery,
} from 'generated/resources/datatable.graphql'
import { BulkSendToPayInvoicesDocument } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'

import { useDataTable } from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SendToPayModal } from 'modules/finance/components/Invoices/SendToPayModal'
import { FailedVendorData } from 'modules/finance/components/Invoices/SendToPayModal/types'
import { isInvoiceEligibleToSendToPay } from 'modules/finance/lib/invoices'

export const DataTableSendToPayBulkAction = () => {
  const { company } = useCurrentSession()
  const dataTable = useDataTable<InvoicesDataTableQuery>()
  const [showSendToPayModal, setShowSendToPayModal] = React.useState<boolean>(false)
  const selectedInvoiceIds = [...dataTable.selectedRows].map((v) => String(v))
  const [submitting, setSubmiting] = React.useState<boolean>(false)

  const eligibleSelectedNodes =
    dataTable.queryResult?.data?.finance.financialDocumentsConnection.edges
      .filter((edge) => selectedInvoiceIds.includes(edge.node.id))
      .map((edge) => edge.node)
      .filter((node) =>
        isInvoiceEligibleToSendToPay({
          currency: node.currency,
          status: node.status,
          validatedAt: node.validatedAt,
        })
      ) || []

  const [bulkSendtoPayInvoices] = useMutation(BulkSendToPayInvoicesDocument, {
    extractorFn: (data) => data.financeMutations.bulkSendToPayFinanceFinancialDocument,
    refetchQueries: [InvoicesDataTableDocument],
  })

  const invoicesReadyToPay = () => {
    return eligibleSelectedNodes.filter((node) => node.vendor?.iban && node.vendor?.legalName)
  }

  const invalidInvoiceVendors = () => {
    const failed = eligibleSelectedNodes.filter(
      (node) => !node.vendor?.iban || !node.vendor?.legalName
    )
    const invoicesByVendor = groupBy(failed, (e) => e.vendor?.id)
    const data = Object.entries(invoicesByVendor).map(([vendorId, invoices]) => {
      const vendorDescription = invoices[0]?.vendor?.legalName || invoices[0]?.vendor?.iban || '-'
      const failedVendor: FailedVendorData = {
        vendorId: String(vendorId),
        vendorDescription: vendorDescription,
        invoicesCounter: invoices.length,
      }
      return failedVendor
    })
    return data
  }

  const submitSendToPay = async (invoiceIds: string[]) => {
    const response = await bulkSendtoPayInvoices({
      variables: {
        ids: invoiceIds,
        companyId: company.id,
      },
    }).then((res) => {
      track(trackingEvents.accounting.invoices.listSendToPay, {
        invoice_count: invoiceIds.length,
      })
      return res
    })
    setSubmiting(false)

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('finance.invoices.actions.send_to_pay_succeed', {
          count: invoicesReadyToPay().length,
        }),
      })
    } else {
      const error = handled.errors[0]
      const message =
        error && 'message' in error
          ? error.message
          : i18n.tp('finance.invoices.actions.send_to_pay_error', {
              count: invoicesReadyToPay().length,
            })
      app.setFlash({
        type: 'wrong',
        message,
      })
    }
  }

  const handleSubmit = () => {
    if (selectedInvoiceIds.length == 0 || eligibleSelectedNodes.length == 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.invoices.actions.send_to_pay_errors.no_selected_validated_items'),
      })
    } else {
      if (invalidInvoiceVendors().length > 0) {
        setShowSendToPayModal(true)
      } else {
        setSubmiting(true)
        submitSendToPay(selectedInvoiceIds)
      }
    }
  }

  const handleOnSuccess = () => {
    setShowSendToPayModal(false)
  }

  return (
    <>
      {showSendToPayModal && (
        <SendToPayModal
          onClose={() => setShowSendToPayModal(false)}
          validInvoiceIds={invoicesReadyToPay().map((v) => v.id)}
          failedVendors={invalidInvoiceVendors()}
          onSuccess={handleOnSuccess}
        />
      )}
      <Button type="secondary" onClick={handleSubmit} iconName="check" disabled={submitting}>
        {i18n.t('finance.invoices.actions.send_to_pay_ok')}
      </Button>
    </>
  )
}
